/* eslint-disable simple-import-sort/imports */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// Import design system styles FIRST, before our local Tailwind utilities index.css
import { CX_BASE_CLASS } from "./design-system.ts";

import "@/i18n";
import "./index.css";

import { getCurrentScreen } from "@auth0/auth0-acul-js";
import { initMixpanel } from "@prophero-customers/analytics";
import { loadSentry } from "./monitoring/sentry.ts";
import App from "./App.tsx";
import { preloadScreen } from "./utils/screen/screenLoader.ts";

async function initializeApp() {
  /**
   * ACUL Integration Note:
   * The following lines handle the specific way this React application is integrated
   * into Auth0's Universal Login page. Auth0 provides the base HTML DOM.
   * This script then creates a 'div' (rootElement),
   * appends it to Auth0's document.body, and then mounts the React application onto this div.
   * This differs from typical setups where an index.html is bundled directly with the app.
   */
  const rootElement = document.createElement("div");
  rootElement.id = "root";
  // No data-theme: CX tokens follow the OS colour scheme (prefers-color-scheme).
  rootElement.className = CX_BASE_CLASS;

  document.body.appendChild(rootElement);

  // Starts the Mixpanel chunk now: when this module runs, the screen's scripts
  // in the page head have all downloaded, so it does not delay the form
  // (CX-902). Events tracked before it is ready are kept and replayed.
  initMixpanel(import.meta.env.VITE_MIXPANEL_TOKEN ?? "", import.meta.env.DEV);

  // Resolve the screen before the first render so it mounts without a Suspense
  // fallback (CX-902). Its chunk is already loaded by the page head; a failure
  // only means the screen falls back to lazy loading.
  if (!import.meta.env.DEV) {
    try {
      await preloadScreen(getCurrentScreen());
    } catch {
      // Falls back to lazy loading inside ProdScreenManager.
    }
  }

  // Mount into the element created above, not getElementById("root"): the dev
  // index.html ships its own #root, which would otherwise win and skip cx-base.
  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>
  );

  // After the first render is scheduled, so the SDK never competes with the
  // screen's own chunks for bandwidth (CX-902).
  setTimeout(() => void loadSentry(), 0);
}

void initializeApp();
